/*
 * EKOA Legal Pages — seamless tab-style navigation.
 *
 * Intercepts clicks on the legal-page navs (desktop sidebar, FAQ nav,
 * mobile announcement bar), fetches the target page in the background,
 * and swaps only #MainContent in place. The URL, page title and
 * back/forward buttons keep working via the History API, so switching
 * between Contact / FAQs / Shipping / Returns / T&Cs / Privacy / Cookie
 * pages feels like changing tabs instead of a full page load.
 *
 * Falls back to a normal navigation if anything goes wrong.
 */
(function () {
  'use strict';

  if (window.__ekoaLegalTabs) return;
  window.__ekoaLegalTabs = true;

  // Never hijack navigation inside the Shopify theme editor.
  if (window.Shopify && window.Shopify.designMode) return;
  if (!window.fetch || !window.history || !window.history.pushState || !window.DOMParser) return;

  var NAV_LINK_SELECTOR = [
    '.ekoa-returns-sidebar a',
    '.ekoa-mobile-announcement-bar a',
  ].join(', ');

  var PAGES_PATH_RE = /(^|\/)pages\//;
  var CACHE_MAX = 12;

  var cache = new Map(); // cache key -> Promise<{title, html, template}>
  var navToken = 0;
  var liveRegion = null;

  var loadedScriptSrcs = new Set();
  Array.prototype.forEach.call(document.scripts, function (script) {
    if (script.src) loadedScriptSrcs.add(script.src);
  });

  injectStyles();

  document.addEventListener('click', onClick);
  document.addEventListener('pointerover', onPrefetch, { passive: true });
  document.addEventListener('touchstart', onPrefetch, { passive: true });
  document.addEventListener('focusin', onPrefetch);
  window.addEventListener('popstate', onPopState);

  function getMain() {
    return document.getElementById('MainContent');
  }

  // Only act while we are on a /pages/... URL, so the same nav links on
  // other templates (e.g. homepage announcement bar) behave normally.
  function onLegalPage() {
    return PAGES_PATH_RE.test(window.location.pathname);
  }

  function eligibleUrl(link) {
    if (!link || !link.href) return null;
    if (link.target && link.target !== '_self') return null;
    if (link.hasAttribute('download')) return null;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (!PAGES_PATH_RE.test(url.pathname)) return null;
    return url;
  }

  function cacheKey(url) {
    return url.pathname + url.search;
  }

  function onClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    var link = event.target.closest(NAV_LINK_SELECTOR);
    if (!link || !onLegalPage()) return;

    var url = eligibleUrl(link);
    if (!url) return;

    if (url.pathname === window.location.pathname) {
      // Same page: let hash links scroll natively, ignore the rest
      // (clicking the active tab shouldn't reload anything).
      if (!url.hash) event.preventDefault();
      return;
    }

    event.preventDefault();
    navigate(url, true);
  }

  function onPrefetch(event) {
    if (!(event.target instanceof Element)) return;

    var link = event.target.closest(NAV_LINK_SELECTOR);
    if (!link || !onLegalPage()) return;

    var url = eligibleUrl(link);
    if (url && url.pathname !== window.location.pathname) fetchPage(url);
  }

  function onPopState(event) {
    if (!event.state || !event.state.ekoaLegalNav) return;

    var url = new URL(window.location.href);
    var scrollY = typeof event.state.scrollY === 'number' ? event.state.scrollY : null;

    navigate(url, false, function () {
      if (scrollY !== null) window.scrollTo(0, scrollY);
    });
  }

  function fetchPage(url) {
    var key = cacheKey(url);
    if (cache.has(key)) return cache.get(key);

    var promise = fetch(url.href, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var main = doc.getElementById('MainContent');
        if (!main) throw new Error('Missing #MainContent');

        return {
          title: doc.title,
          html: main.innerHTML,
          template: main.getAttribute('data-template'),
        };
      });

    promise.catch(function () {
      cache.delete(key);
    });

    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
    cache.set(key, promise);
    return promise;
  }

  function navigate(url, push, onDone) {
    var main = getMain();
    if (!main) {
      window.location.assign(url.href);
      return;
    }

    var token = ++navToken;
    main.classList.add('ekoa-legal-loading');

    fetchPage(url)
      .then(function (page) {
        if (token !== navToken) return;

        if (push) {
          // Remember where we are (incl. scroll) before moving on, so the
          // browser back button restores this entry seamlessly too.
          var currentState = window.history.state || {};
          currentState.ekoaLegalNav = true;
          currentState.scrollY = window.scrollY;
          window.history.replaceState(currentState, '', window.location.href);
          window.history.pushState({ ekoaLegalNav: true }, '', url.href);
          if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }
        }

        render(main, page);
        if (onDone) onDone();
      })
      .catch(function () {
        if (token !== navToken) return;
        // Anything unexpected: do a regular full navigation instead.
        window.location.assign(url.href);
      });
  }

  function render(main, page) {
    main.innerHTML = page.html;
    if (page.template) main.setAttribute('data-template', page.template);
    executeScripts(main);

    document.title = page.title;

    main.classList.remove('ekoa-legal-loading');
    main.classList.remove('ekoa-legal-swap-in');
    void main.offsetWidth;
    main.classList.add('ekoa-legal-swap-in');

    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    try {
      main.focus({ preventScroll: true });
    } catch (error) {
      /* older browsers without focus options */
    }

    announce(page.title);
    window.dispatchEvent(
      new CustomEvent('ekoa:legal-nav:rendered', { detail: { url: window.location.href } })
    );
  }

  // Scripts inserted via innerHTML never execute, so re-create each one.
  // External scripts already loaded once (e.g. this file) are skipped —
  // section scripts are written to be safe to re-run.
  function executeScripts(container) {
    var scripts = container.querySelectorAll('script');

    Array.prototype.forEach.call(scripts, function (oldScript) {
      var type = (oldScript.getAttribute('type') || '').toLowerCase();
      var isJs =
        type === '' || type === 'module' || type === 'text/javascript' || type === 'application/javascript';
      if (!isJs) return;

      if (oldScript.src && loadedScriptSrcs.has(oldScript.src)) return;

      var newScript = document.createElement('script');
      Array.prototype.forEach.call(oldScript.attributes, function (attribute) {
        newScript.setAttribute(attribute.name, attribute.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);

      if (newScript.src) loadedScriptSrcs.add(newScript.src);
    });
  }

  function announce(title) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.style.cssText =
        'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = title;
  }

  function injectStyles() {
    var style = document.createElement('style');
    style.textContent = [
      // Dim only when a fetch actually takes a moment (the transition delay
      // keeps instant cache hits completely flicker-free).
      '#MainContent.ekoa-legal-loading{opacity:.55;transition:opacity .18s ease .12s;}',
      '@keyframes ekoa-legal-swap-in{from{opacity:.4;}to{opacity:1;}}',
      '#MainContent.ekoa-legal-swap-in{animation:ekoa-legal-swap-in .2s ease-out;}',
      '@media (prefers-reduced-motion: reduce){',
      '#MainContent.ekoa-legal-loading{transition:none;}',
      '#MainContent.ekoa-legal-swap-in{animation:none;}',
      '}',
    ].join('');
    document.head.appendChild(style);
  }
})();
